import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldOff, Shield, Users, Activity, LogOut, RefreshCw } from 'lucide-react';

const ROLE_LABELS = {
    clinician:         'Clinician',
    lab_tech:          'Lab Tech',
    rad_tech:          'Rad Tech',
    biller:            'Biller',
    medical_assistant: 'Medical Assistant',
    office_assistant:  'Office Assistant',
    admin:             'Admin',
};

const ACTION_OPTIONS = ['Query', 'Changed', 'Added', 'Deleted', 'Signed', 'Printed', 'Error'];

function StatCard({ icon, label, value, sub, color }) {
    return (
        <div className="sec-stat-card">
            <div className="sec-stat-icon" style={{ background: color }}>{icon}</div>
            <div>
                <div className="sec-stat-value">{value}</div>
                <div className="sec-stat-label">{label}</div>
                {sub && <div className="sec-stat-sub">{sub}</div>}
            </div>
        </div>
    );
}

export default function AdminSecurityTab({ BASE_URL, currentUser, user }) {
    const [stats, setStats]             = useState(null);
    const [sessions, setSessions]       = useState([]);
    const [auditLog, setAuditLog]       = useState([]);
    const [auditFilter, setAuditFilter] = useState('');
    const [auditSearch, setAuditSearch] = useState('');
    const [loading, setLoading]         = useState(true);
    const [revokeMsg, setRevokeMsg]     = useState('');
    const [globalEnforced, setGlobalEnforced]   = useState(false);
    const [enforcementMsg, setEnforcementMsg]   = useState('');
    const [enforcementLoading, setEnforcementLoading] = useState(false);

    const userId = user || currentUser?.admin_id || currentUser?.clinicians_id;
    const userType = currentUser?.type;

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [sRes, sessRes, logRes, enfRes] = await Promise.all([
                fetch(`${BASE_URL}/admin/security/stats?user=${userId}&type=${userType}`),
                fetch(`${BASE_URL}/admin/security/sessions?user=${userId}&type=${userType}`),
                fetch(`${BASE_URL}/admin/security/auditLog?limit=100${auditFilter ? `&action=${auditFilter}` : ''}`),
                fetch(`${BASE_URL}/admin/security/globalEnforcement`),
            ]);
            const [s, sess, log, enf] = await Promise.all([sRes.json(), sessRes.json(), logRes.json(), enfRes.json()]);
            setStats(s);
            setSessions(Array.isArray(sess) ? sess : []);
            setAuditLog(Array.isArray(log) ? log : []);
            setGlobalEnforced(!!enf.enabled);
        } catch (err) {
            logger.error('Security tab fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [BASE_URL, userId, userType, auditFilter]);

    const handleGlobalEnforcementToggle = async () => {
        const next = !globalEnforced;
        const confirmed = window.confirm(
            next
                ? 'Enable global MFA enforcement?\n\nAll staff will be required to use MFA at their next login. Those without MFA set up will be redirected to enroll.'
                : 'Disable global MFA enforcement?\n\nStaff without per-user enforcement will no longer be required to use MFA.'
        );
        if (!confirmed) return;

        setEnforcementLoading(true);
        setEnforcementMsg('');
        try {
            const res = await fetch(`${BASE_URL}/admin/security/globalEnforcement`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: next, user: userId, type: userType }),
            });
            const data = await res.json();
            if (res.ok) {
                setGlobalEnforced(data.enabled);
                setEnforcementMsg(`Global MFA enforcement ${data.enabled ? 'enabled' : 'disabled'}.`);
            } else {
                setEnforcementMsg(data.error || 'Failed to update enforcement.');
            }
        } catch {
            setEnforcementMsg('Network error. Please try again.');
        } finally {
            setEnforcementLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleRevoke = async (session_token, full_name) => {
        if (!window.confirm(`Revoke session for ${full_name || 'this user'}?\n\nThey will be logged out immediately.`)) return;
        setRevokeMsg('');
        try {
            const res = await fetch(`${BASE_URL}/admin/security/revokeSession`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_token, user: userId, type: userType }),
            });
            const data = await res.json();
            if (res.ok) {
                setRevokeMsg(`Session revoked for ${full_name || 'user'}.`);
                setSessions(prev => prev.filter(s => s.session_token !== session_token));
                setStats(prev => prev ? { ...prev, active_sessions: prev.active_sessions - 1 } : prev);
            } else {
                setRevokeMsg(data.error || 'Revoke failed.');
            }
        } catch {
            setRevokeMsg('Network error during revoke.');
        }
    };

    const mfaPct = stats ? Math.round((stats.mfa_enabled / (stats.total_staff || 1)) * 100) : 0;

    const filteredLog = auditLog.filter(e =>
        !auditSearch ||
        e.note?.toLowerCase().includes(auditSearch.toLowerCase()) ||
        e.performed_by_type?.toLowerCase().includes(auditSearch.toLowerCase()) ||
        String(e.performed_by_id).includes(auditSearch)
    );

    const formatDate = (raw) => {
        if (!raw) return '—';
        const d = new Date(raw);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return <div className="sec-loading">Loading security data…</div>;

    return (
        <div className="sec-tab">

            {/* ── Global MFA enforcement toggle ── */}
            <div className={`sec-enforcement-banner ${globalEnforced ? 'sec-enforcement-on' : 'sec-enforcement-off'}`}>
                <div className="sec-enforcement-info">
                    {globalEnforced
                        ? <ShieldCheck size={22} />
                        : <Shield size={22} />}
                    <div>
                        <div className="sec-enforcement-title">
                            Global MFA Enforcement is <strong>{globalEnforced ? 'ON' : 'OFF'}</strong>
                        </div>
                        <div className="sec-enforcement-sub">
                            {globalEnforced
                                ? 'All staff are required to use MFA. Users without MFA will be redirected to enroll at next login.'
                                : 'MFA is optional unless enforced per user. Toggle on to require MFA for everyone.'}
                        </div>
                    </div>
                </div>
                <button
                    className={`sec-enforcement-btn ${globalEnforced ? 'sec-enforcement-btn-off' : 'sec-enforcement-btn-on'}`}
                    onClick={handleGlobalEnforcementToggle}
                    disabled={enforcementLoading}
                >
                    {enforcementLoading ? 'Updating…' : globalEnforced ? 'Disable Enforcement' : 'Enable Enforcement'}
                </button>
            </div>
            {enforcementMsg && (
                <p className={enforcementMsg.includes('enabled') ? 'admin-merge-success' : 'admin-merge-error'}>
                    {enforcementMsg}
                </p>
            )}

            {/* ── Stat cards ── */}
            <div className="sec-stats-row">
                <StatCard
                    icon={<Users size={20} color="white" />}
                    label="Active Staff"
                    value={stats?.total_staff ?? '—'}
                    color="#3b82f6"
                />
                <StatCard
                    icon={<ShieldCheck size={20} color="white" />}
                    label="MFA Enabled"
                    value={stats?.mfa_enabled ?? '—'}
                    sub={`${mfaPct}% of staff`}
                    color="#16a34a"
                />
                <StatCard
                    icon={<ShieldOff size={20} color="white" />}
                    label="MFA Disabled"
                    value={stats ? stats.total_staff - stats.mfa_enabled : '—'}
                    sub={`${100 - mfaPct}% of staff`}
                    color="#6b7280"
                />
                <StatCard
                    icon={<Activity size={20} color="white" />}
                    label="Active Sessions"
                    value={stats?.active_sessions ?? '—'}
                    color="#f59e0b"
                />
                <StatCard
                    icon={<Activity size={20} color="white" />}
                    label="Events (24h)"
                    value={stats?.recent_events ?? '—'}
                    color="#8b5cf6"
                />
            </div>

            {/* ── Active Sessions ── */}
            <div className="sec-section">
                <div className="sec-section-header">
                    <h2 className="sec-section-title">Active Sessions</h2>
                    <button className="sec-refresh-btn" onClick={fetchAll} title="Refresh">
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
                {revokeMsg && (
                    <p className={revokeMsg.includes('revoked') ? 'admin-merge-success' : 'admin-merge-error'}>
                        {revokeMsg}
                    </p>
                )}
                {sessions.length === 0 ? (
                    <p className="sec-empty">No active sessions.</p>
                ) : (
                    <table className="admin-staff-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Expires</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s, i) => (
                                <tr key={i}>
                                    <td>{s.full_name || <span style={{ color: '#adb5bd' }}>Unknown</span>}</td>
                                    <td>
                                        <span className={`role-badge role-${s.role}`}>
                                            {ROLE_LABELS[s.role] || s.role}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: '0.85rem', color: '#495057' }}>{formatDate(s.session_expiration)}</td>
                                    <td>
                                        <button
                                            className="sec-revoke-btn"
                                            title={`Revoke session for ${s.full_name}`}
                                            onClick={() => handleRevoke(s.session_token, s.full_name)}
                                        >
                                            <LogOut size={14} /> Revoke
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Audit Log ── */}
            <div className="sec-section">
                <div className="sec-section-header">
                    <h2 className="sec-section-title">Audit Log</h2>
                    <div className="sec-log-filters">
                        <select
                            className="admin-role-select"
                            value={auditFilter}
                            onChange={e => setAuditFilter(e.target.value)}
                        >
                            <option value="">All Actions</option>
                            {ACTION_OPTIONS.map(a => (
                                <option key={a} value={a}>{a}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            placeholder="Search notes or staff…"
                            value={auditSearch}
                            onChange={e => setAuditSearch(e.target.value)}
                            className="admin-search-input"
                        />
                        <span className="admin-count">{filteredLog.length} entries</span>
                    </div>
                </div>

                {filteredLog.length === 0 ? (
                    <p className="sec-empty">No log entries found.</p>
                ) : (
                    <table className="admin-staff-table sec-log-table">
                        <thead>
                            <tr>
                                <th>Date &amp; Time</th>
                                <th>Performed By</th>
                                <th>Action</th>
                                <th>Note</th>
                                <th>Patient</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLog.map((e) => (
                                <tr key={e.id}>
                                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#6c757d' }}>
                                        {formatDate(e.record_date)}
                                    </td>
                                    <td>
                                        {e.performed_by_type ? (
                                            <span>
                                                <span className={`role-badge role-${e.performed_by_type}`} style={{ marginRight: 6 }}>
                                                    {ROLE_LABELS[e.performed_by_type] || e.performed_by_type}
                                                </span>
                                                <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>#{e.performed_by_id}</span>
                                            </span>
                                        ) : <span style={{ color: '#adb5bd' }}>—</span>}
                                    </td>
                                    <td>
                                        <span className={`sec-action-badge sec-action-${e.action?.toLowerCase()}`}>
                                            {e.action}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: '0.85rem', maxWidth: 420, wordBreak: 'break-word' }}>{e.note}</td>
                                    <td style={{ fontSize: '0.85rem', color: '#495057' }}>{e.patient_name || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
